import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { approvalService } from 'src/services/api/approval.service'
import { enrichDetailedApproval } from 'src/services/helpers/approval.helper'
import { useAccountStore } from 'src/stores/account'
import { useCompaniesDirectory } from 'src/composables/account/query/useCompaniesDirectory'
import { CACHE_ACTIONS } from 'src/services/consts/app.const'
import { broadcastApproval } from 'src/composables/app/system/useBroadcastChannel'
import { refetchEnabledWhenVisible } from 'src/composables/app/system/useOnlineStatus'

// `client` is the Vue Query client from the query context. The add-or-update check reads the
// real account entry, not the approvals store, which reads the example set during a walkthrough.
export async function approvalQueryFn({ queryKey, client }) {
  const accountStore = useAccountStore()

  const [, approvalIdFromKey] = queryKey
  const approvalId = approvalIdFromKey?.value ?? approvalIdFromKey

  if (!approvalId) return null

  const approval = await approvalService.getApprovalById(approvalId)

  if (approval) {
    broadcastApproval(approvalId, approval)
    const realApprovals = client.getQueryData([QUERY_KEYS.ACCOUNT_DATA])?.approvals || []
    const exists = realApprovals.some((a) => a.approvalId === approval.approvalId)
    accountStore.updateAccountApprovals(exists ? CACHE_ACTIONS.UPDATE : CACHE_ACTIONS.ADD, approval)
  }

  return approval
}

export function useApprovalQuery(approvalIdRef) {
  const companyDictionary = useCompaniesDirectory()
  const accountStore = useAccountStore()
  const accountApprovals = computed(() => accountStore.accountData?.approvals || [])

  const enableQuery = computed(() => !!approvalIdRef?.value && !!accountStore.accountId)

  const approvalQuery = useQuery({
    queryKey: [QUERY_KEYS.APPROVAL, approvalIdRef],
    enabled: enableQuery,
    refetchOnWindowFocus: enableQuery,
    refetchOnReconnect: refetchEnabledWhenVisible(enableQuery),
    queryFn: approvalQueryFn,
  })

  const isApprovalLoading = computed(
    () => approvalQuery.isLoading.value || !accountStore.accountId || accountStore.isLoading
  )
  const approval = computed(() => {
    const detailedApproval = approvalQuery.data.value
    if (!detailedApproval) return null

    const enriched = enrichDetailedApproval(
      detailedApproval,
      accountApprovals.value,
      companyDictionary
    )

    const accountApproval = accountApprovals.value.find((a) => a.approvalId === enriched.approvalId)
    if (accountApproval?.lastUserConfirmation || accountApproval?.nextUserConfirmation) {
      return {
        ...enriched,
        lastUserConfirmation: accountApproval.lastUserConfirmation ?? enriched.lastUserConfirmation,
        nextUserConfirmation: accountApproval.nextUserConfirmation ?? enriched.nextUserConfirmation,
      }
    }

    return enriched
  })

  return {
    approval,
    isApprovalLoading,
    isApprovalRefetching: approvalQuery.isRefetching,
    approvalError: approvalQuery.error,
    refetchApproval: approvalQuery.refetch,
  }
}
